import type { SxProps } from '@mui/material';

import type { DataTableColumnMeta } from './dataTable.types';

/** TanStack column / header / cell `column` shape for reading `columnDef.meta.dataTable`. */
export type DataTableColumnLike = {
  columnDef: { meta?: { dataTable?: DataTableColumnMeta } };
};

export function getDataTableColumnMeta(
  column: DataTableColumnLike,
): DataTableColumnMeta | undefined {
  return column.columnDef.meta?.dataTable;
}

function cssSize(value: number | string): string | number {
  return typeof value === 'number' ? `${value}px` : value;
}

/**
 * Layout `sx` from {@link DataTableColumnMeta} (`width`, `minWidth`, `maxWidth`, `compact`).
 * Merged onto table head/body cells after design-system defaults.
 */
export function getDataTableColumnMetaSx(
  meta: DataTableColumnMeta | undefined,
): SxProps | undefined {
  if (meta == null) {
    return undefined;
  }
  const sx: Record<string, unknown> = {};
  if (meta.width != null) {
    sx.width = cssSize(meta.width);
  }
  if (meta.minWidth != null) {
    sx.minWidth = cssSize(meta.minWidth);
  }
  if (meta.maxWidth != null) {
    sx.maxWidth = cssSize(meta.maxWidth);
  }

  return Object.keys(sx).length > 0 ? (sx as SxProps) : undefined;
}

export type DataTableCellPropsFromMeta = {
  right: boolean;
  center: boolean;
  number: boolean;
  isFirst: boolean;
  sx?: SxProps;
};

/**
 * Maps {@link DataTableColumnMeta} to `TableHeadCell` / `TableCell` props (alignment, numeric, `sx`).
 * Head and body share one implementation so layout rules stay in sync.
 */
export function dataTableMetaToCellProps(
  meta: DataTableColumnMeta | undefined,
): DataTableCellPropsFromMeta {
  const metaSx = getDataTableColumnMetaSx(meta);
  return {
    right: meta?.align === 'right',
    center: meta?.align === 'center',
    number: Boolean(meta?.numeric),
    isFirst: Boolean(meta?.isFirst),
    ...(metaSx != null ? { sx: metaSx } : {}),
  };
}
